import { Alert, AlertTitle, Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { TopicRecord } from "@/shared/domain/topics";

import { MessagePathsDetail } from "../MessagePathsDetail";
import { RobotoDataGrid } from "../RobotoDataGrid";

import { ColumnsModal } from "./ColumnsModal";
import { TablePageAction, TablePageState } from "./stateReducer";
import { createRows, retrieveMetadataColumns } from "./tableLogic";
import type { BaseTableProps } from "./tableProps";

const possibleStandardFilesColumns = [
  "topic_id",
  "topic_name",
  "created",
  "created_by",
  "modified",
  "modified_by",
  "schema_name",
  "org_id",
  "message_count",
  "start_time",
  "end_time",
];

const sortableColumns = new Set<string>();

interface TopicsTableProps extends BaseTableProps<TopicRecord> {
  selectedRows?: Set<string>;
  setSelectedRows?: (arg: Set<string>) => void;
  onRowSingleClick?: (topic: TopicRecord) => void;
  variant?: "elevation" | "outlined";
  topicsTableState: TablePageState;
  topicsTableDispatch: React.Dispatch<TablePageAction>;
}

export const TopicsTable: React.FC<TopicsTableProps> = ({
  data: topicRecords,
  dataCount,
  loading,
  selectedRows,
  setSelectedRows = () => {},
  onRowSingleClick = () => {},
  variant,
  containerStyle,
  fetchMoreData,
  topicsTableState: state,
  topicsTableDispatch: dispatch,
}) => {
  const theme = useTheme();

  const [page, setPage] = React.useState(0);

  const topicsById = new Map<string, TopicRecord>(
    topicRecords.map((topic) => {
      const idToStr = topic.topic_id.toString();
      return [idToStr, topic];
    }),
  );

  const { goto } = useNavigation();

  const [selectedRowsToggle, setSelectedRowsToggle] =
    React.useState<boolean>(false);

  const pageData = React.useMemo(() => {
    return topicRecords.slice(
      page * state.rowsPerPage,
      page * state.rowsPerPage + state.rowsPerPage,
    );
  }, [topicRecords, state.rowsPerPage, page]);

  const { rows, allPossibleColumns } = React.useMemo(() => {
    const possibleMetadataColumns: string[] =
      retrieveMetadataColumns(pageData) ?? [];

    const rows = createRows(pageData, state.columns, possibleMetadataColumns);

    const allPossibleColumns = possibleStandardFilesColumns
      .concat(possibleMetadataColumns)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    return { rows, allPossibleColumns };
  }, [pageData, state.columns]);

  const noSearchResults =
    Object.keys(state.lastSearchQuery).length > 0 && topicRecords.length === 0;

  React.useEffect(() => {
    if (selectedRows && selectedRows.size === 0) {
      setSelectedRowsToggle((prevToggle) => !prevToggle);
      dispatch({
        type: "SET_SELECTED_ROWS",
        payload: { selectedRows: new Set<string>() },
      });
    }
  }, [dispatch, selectedRows]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <Paper
          sx={{
            mt: theme.spacing(2),
          }}
          variant={variant}
        >
          {noSearchResults && (
            <Alert severity="info">
              <AlertTitle>Topics</AlertTitle>
              <>
                No topics match your search criteria. Try changing your search
                terms and try again.
              </>
            </Alert>
          )}

          {!noSearchResults && (
            <>
              <RobotoDataGrid
                sortableColumns={sortableColumns}
                sortingOrder={state.sortOrder}
                sortingColumnIndex={state.sortColumnIndex}
                page={page}
                rowsPerPage={state.rowsPerPage}
                nextPageButtonDisabled={
                  (page + 1) * state.rowsPerPage >= topicRecords.length
                }
                containerStyle={containerStyle}
                loading={loading}
                columnNames={state.columns}
                rows={rows}
                totalRowCount={dataCount}
                onPageChange={(newPage, rowsPerPage) => {
                  if ((newPage + 2) * rowsPerPage >= topicRecords.length) {
                    // Request more pages of data from the backend
                    // as we approach the last page of cached results.
                    // 2 pages prior seemed to work well experimentally.
                    fetchMoreData?.();
                  }
                  setPage(newPage);
                }}
                onRowsPerPageChange={(_page, newRowsPerPage) => {
                  setPage(0);
                  dispatch({
                    type: "SET_TABLE_CONFIG",
                    payload: {
                      rowsPerPage: newRowsPerPage,
                    },
                  });
                }}
                onColumnSortClicked={() => {
                  // do nothing
                }}
                onAddColumnClick={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: true });
                }}
                onRowDoubleClick={(rowId) => {
                  const t = topicsById.get(rowId) || null;
                  if (t && t.association) {
                    goto.visualize({
                      dataSource: {
                        files: [t.association.association_id],
                      },
                      params: {
                        filterTerm: t.topic_name,
                      },
                    });
                  }
                }}
                onRowSingleClick={(rowId: string) => {
                  const topicRecord = topicsById.get(rowId);
                  if (onRowSingleClick && topicRecord) {
                    onRowSingleClick(topicRecord);
                  }
                }}
                onSelectedRowsChange={(selectedRows) => {
                  setSelectedRows(selectedRows);
                  dispatch({
                    type: "SET_SELECTED_ROWS",
                    payload: { selectedRows: selectedRows },
                  });
                }}
                isRowSelectable={false}
                isRowExpandable={true}
                clearSelectedRowsToggle={selectedRowsToggle}
                expandableContent={(rowId: string) => {
                  const partialTopicRecord = topicsById.get(rowId);
                  if (partialTopicRecord) {
                    return (
                      <Box
                        sx={{
                          padding: theme.spacing(3, 2),
                        }}
                      >
                        <MessagePathsDetail
                          topicId={partialTopicRecord.topic_id}
                        />
                      </Box>
                    );
                  }
                }}
              />
              <ColumnsModal
                open={state.modalOpen}
                handleClose={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: false });
                }}
                columns={allPossibleColumns}
                currentColumns={state.columns}
                onSave={(updatedColumns) => {
                  dispatch({ type: "SAVE_COLUMNS", payload: updatedColumns });
                }}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
